import {
  BriefcaseIcon,
  ChartSquareBarIcon,
  EyeIcon,
  LightningBoltIcon,
  PlayIcon,
} from "@heroicons/react/outline";

import Square3Stack3DIcon from "@/assets/Square3Stack3DIcon";
import { getBuildTimeEnv, getWindowEnv, RuntimeEnvKey } from "@/utils/env";

export const SESSION_STORAGE = {
  SETTINGS_RETURN_URL: "settings_return_url",
  RECENTLY_VIEWED_REQUEST_LOG_ID: "recently_viewed_request_log_id",
  RECENTLY_VIEWED_TRACE_ID: "recently_viewed_trace_id",
  REQUESTS_ANALYTICS_RETURN_URL: "requests_analytics_return_url",
  TRACES_RETURN_URL: "traces_return_url",
};

export const LOCAL_STORAGE = {
  ACCESS_TOKEN: "ACCESS_TOKEN",
  ACTIVE_WORKSPACE_ID: "ACTIVE_WORKSPACE_ID",
  INPUT_VARIABLE_ROWS: "INPUT_VARIABLE_ROWS",
  POSTHOG_PLAN_TYPE: "POSTHOG_PLAN_TYPE",
  REQUEST_DISPLAY_VIEW: "REQUEST_DISPLAY_VIEW",
  USER: "USER",
  LAST_METADATA_FIELD_FILTER: "LAST_METADATA_FIELD_FILTER",
  LAST_TEMPLATE_FORMAT: "LAST_TEMPLATE_FORMAT",
  WS_TOKEN: "WS_TOKEN",
};

export const NAVIGATION: {
  name: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon: any;
  href?: string;
  altHrefs?: string[];
  items?: {
    name: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    icon?: any;
    description?: string;
    href: string;
    altHrefs?: string[];
  }[];
}[] = [
  { name: "Playground", icon: PlayIcon, href: "playground" },
  { name: "Signals", icon: LightningBoltIcon, href: "signals" },
];

export const MARKETING_NAVIGATION: {
  label: string;
  route?: string;
  dropdownItems?: {
    label: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    icon: any;
    route: string;
  }[];
}[] = [
  {
    label: "Platform",
    dropdownItems: [
      {
        route: "/platform/prompt-management",
        label: "Prompt Management",
        icon: Square3Stack3DIcon,
      },
      {
        route: "/platform/evaluations",
        label: "Evaluations",
        icon: ChartSquareBarIcon,
      },
      {
        route: "/platform/observability",
        label: "Observability",
        icon: EyeIcon,
      },
      {
        route: "/platform/dataset-management",
        label: "Dataset Management",
        icon: BriefcaseIcon,
      },
    ],
  },
  { label: "Docs", route: "https://docs.promptlayer.com/" },
  { label: "Blog", route: "https://blog.promptlayer.com/" },
  { label: "Case Studies", route: "/case-studies" },
];

export const queryKeys = {
  requests: "requests",
  request: "request",
  requestBulk: "request-bulk",
  authUser: "authUser",
  metadataFields: "metadataFields",
  scoreNames: "scoreNames",
  spans: "spans",
  traces: "traces",
  structuredSearchSuggestions: "structuredSearchSuggestions",
  signals: "signals",
};

export interface PlanDetails {
  expiryLengthDays: number | null;
  requestCount: number;
  evaluateReports: boolean;
}

export const WEBSOCKET_MESSAGE_NAME = {
  APPEND_TO_TEMPLATE: "APPEND_TO_TEMPLATE",
  CODE_INTERPRETER_COMPLETED: "CODE_INTERPRETER_COMPLETED",
  CODE_INTERPRETER_STARTED: "CODE_INTERPRETER_STARTED",
  CODE_INTERPRETER_IN_PROGRESS: "CODE_INTERPRETER_IN_PROGRESS",
  CREATE_NEW_MESSAGE: "CREATE_NEW_MESSAGE",
  INDIVIDUAL_RUN_COMPLETE: "INDIVIDUAL_RUN_COMPLETE",
  UPDATE_LAST_MESSAGE: "UPDATE_LAST_MESSAGE",
  DATASET_FILE_PROCESSING_COMPLETE: "DATASET_FILE_PROCESSING_COMPLETE",
  SET_WORKFLOW_NODE_OUTPUT: "SET_WORKFLOW_NODE_OUTPUT",
  SET_EDGE_OUTPUT: "SET_EDGE_OUTPUT",
  DATASET_FILE_PROCESSING_PROGRESS: "DATASET_FILE_PROCESSING_PROGRESS",
  REPORT_PROCESSING_PROGRESS: "REPORT_PROCESSING_PROGRESS",
  REPORT_FINISHED: "REPORT_FINISHED",
  SHELL_STARTED: "SHELL_STARTED",
  SHELL_IN_PROGRESS: "SHELL_IN_PROGRESS",
  SHELL_COMPLETED: "SHELL_COMPLETED",
  APPLY_PATCH_STARTED: "APPLY_PATCH_STARTED",
  APPLY_PATCH_IN_PROGRESS: "APPLY_PATCH_IN_PROGRESS",
  APPLY_PATCH_COMPLETED: "APPLY_PATCH_COMPLETED",
  TOOL_SEARCH_STARTED: "TOOL_SEARCH_STARTED",
  TOOL_SEARCH_SEARCHING: "TOOL_SEARCH_SEARCHING",
  TOOL_SEARCH_COMPLETED: "TOOL_SEARCH_COMPLETED",
  WORKFLOW_TEST_RUN_KICKED_OFF: "WORKFLOW_TEST_RUN_KICKED_OFF",
  WORKFLOW_TEST_RUN_UPDATE: "WORKFLOW_TEST_RUN_UPDATE",
  WORKFLOW_TEST_RUN_SUCCEEDED: "WORKFLOW_TEST_RUN_SUCCEEDED",
  WORKFLOW_TEST_RUN_FAILED: "WORKFLOW_TEST_RUN_FAILED",
  REPORT_SCORE_CALCULATED: "REPORT_SCORE_CALCULATED",
  NOTIFICATION_CREATED: "NOTIFICATION_CREATED",
  ENTITY_PREVIEW_UPDATED: "ENTITY_PREVIEW_UPDATED",
  SMART_TABLE_SHEET_CREATED: "SMART_TABLE_SHEET_CREATED",
};

export const MAX_SIDEBAR_NEW_REQUESTS_THRESHOLD = Number(
  import.meta.env.REACT_APP_MAX_SIDEBAR_NEW_REQUESTS_THRESHOLD ?? 10,
);

export const SIDEBAR_MAX = 500;

export const IS_RECAPTCHA_ENABLED = import.meta.env.REACT_APP_IS_RECAPTCHA_ENABLED === "true";

export const IS_RECAPTCHA_ENABLED_PLAYGROUND =
  import.meta.env.REACT_APP_IS_RECAPTCHA_ENABLED_PLAYGROUND === "true";

export const MAINTENANCE_BANNER_MESSAGE = import.meta.env.REACT_APP_MAINTENANCE_BANNER_MESSAGE;

export const IS_GOOGLE_TAG_MANAGER_ENABLED =
  (import.meta.env.REACT_APP_IS_GOOGLE_TAG_MANAGER_ENABLED ?? "false") === "true";

export const IS_AI_FEATURES_ENABLED =
  (import.meta.env.REACT_APP_IS_AI_FEATURES_ENABLED ?? "true") === "true";

export const IS_CLEARBITS_ENABLED =
  (import.meta.env.REACT_APP_IS_CLEARBITS_ENABLED ?? "false") === "true";

export const IS_LEADSY_ENABLED =
  (import.meta.env.REACT_APP_IS_LEADSY_ENABLED ?? "false") === "true";

export const IS_REDDIT_PIXEL_ENABLED =
  (import.meta.env.REACT_APP_IS_REDDIT_PIXEL_ENABLED ?? "false") === "true";

export interface AuthConfig {
  isOnPrem?: boolean;
  hasSSO?: boolean;
  companyName?: string;
  companyLogo?: string;
}

const DEFAULT_AUTH_CONFIG: Required<AuthConfig> = {
  isOnPrem: false,
  hasSSO: false,
  companyName: "",
  companyLogo: "",
};

const parseAuthConfig = (
  authConfigEnv: string | undefined,
  source: string,
): AuthConfig | undefined => {
  if (authConfigEnv) {
    try {
      const cleaned = authConfigEnv.trim().replace(/^["']|["']$/g, "");
      const parsed = JSON.parse(cleaned) as AuthConfig;

      if (typeof parsed === "object" && parsed !== null) {
        return parsed;
      }
    } catch {
      // eslint-disable-next-line no-console
      console.error(`Failed to parse ${source} AUTH_CONFIG`);
    }
  }

  return undefined;
};

export const getAuthConfig = (): AuthConfig => {
  const buildTimeAuthConfig = parseAuthConfig(
    getBuildTimeEnv(RuntimeEnvKey.AuthConfig),
    "build-time",
  );
  const runtimeAuthConfig = parseAuthConfig(getWindowEnv(RuntimeEnvKey.AuthConfig), "runtime");
  const mergedAuthConfig = {
    ...DEFAULT_AUTH_CONFIG,
    ...(buildTimeAuthConfig ?? {}),
    ...(runtimeAuthConfig ?? {}),
  };

  return {
    isOnPrem: mergedAuthConfig.isOnPrem ?? DEFAULT_AUTH_CONFIG.isOnPrem,
    hasSSO: mergedAuthConfig.hasSSO ?? DEFAULT_AUTH_CONFIG.hasSSO,
    companyName: mergedAuthConfig.companyName ?? DEFAULT_AUTH_CONFIG.companyName,
    companyLogo: mergedAuthConfig.companyLogo ?? DEFAULT_AUTH_CONFIG.companyLogo,
  };
};

export const AUTH_CONFIG = getAuthConfig();
